import { useEffect, useRef } from 'react';

/**
 * Calls `onHidden` as soon as the page is hidden while a recording is in progress — app switch, incoming
 * call, tab change, navigation away.
 *
 * The recorder's duration cutoff is a timer, and timers are throttled (on iOS, suspended outright) while
 * a page is backgrounded, so a recording started before the switch could otherwise run well past the
 * limit. `visibilitychange` fires before the platform suspends the page, which makes it the last reliable
 * moment to stop. See CHANGE-97165.
 */
export function useStopRecordingWhenHidden(isRecording: boolean, onHidden: () => void): void {
	// Kept in a ref so the listener effect can depend on `isRecording` alone: re-subscribing on every
	// render would be wasteful, and callers rebuild `onHidden` each render.
	const onHiddenRef = useRef(onHidden);
	useEffect(() => {
		onHiddenRef.current = onHidden;
	}, [onHidden]);

	useEffect(() => {
		if (!isRecording) {
			return () => {};
		}

		const handleVisibilityChange = () => {
			if (document.visibilityState !== 'hidden') return;
			onHiddenRef.current();
		};

		/**
		 * Calls `onHidden`. `pagehide` covers the cases `visibilitychange` misses, notably iOS Safari
		 * moving the page into the back/forward cache.
		 */
		const handlePageHide = () => {
			onHiddenRef.current();
		};

		document.addEventListener('visibilitychange', handleVisibilityChange);
		window.addEventListener('pagehide', handlePageHide);

		// The page can already be hidden by the time this subscribes: a render sits between
		// `MediaRecorder.onstart` and this effect, so a `visibilitychange` in that gap is missed, and
		// returning to the page only fires a `visible` event that `handleVisibilityChange` ignores.
		if (document.visibilityState === 'hidden') {
			onHiddenRef.current();
		}

		return () => {
			document.removeEventListener('visibilitychange', handleVisibilityChange);
			window.removeEventListener('pagehide', handlePageHide);
		};
	}, [isRecording]);
}
